import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Modal from "Components/Common/Modal";
import Stepper from "common/Stepper";
import { useFormik } from "formik";
import * as Yup from "yup";
import { get, omit, pick } from "lodash";

import ConsumerDetails from "Components/MeterInfoForm/ConsumerDetails";
import MeterDetails from "Components/MeterInfoForm/MeterDetails";
import SealDetails from "Components/MeterInfoForm/SealDetails";
import ServiceConnectionDetails from "Components/MeterInfoForm/ServiceConnectionDetails";
import AccucheckTest from "Components/MeterInfoForm/AccucheckTest";
import MeterReading from "Components/MeterInfoForm/MeterReading";
import SolarMeterReading from "Components/MeterInfoForm/SolarMeterReading";
import Backdrop from "Components/Common/Backdrop";

import { inputFields as meterDetailsFields } from "Components/MeterInfoForm/MeterDetails";
import { inputFields as sealDetailsFields } from "Components/MeterInfoForm/SealDetails";
import { inputFields as serviceConnectionDetailsFields } from "Components/MeterInfoForm/ServiceConnectionDetails";
import { inputFields as accucheckTestFields } from "Components/MeterInfoForm/AccucheckTest";
import { inputFields as meterReadingFields } from "Components/MeterInfoForm/MeterReading";
import { inputFields as solarMeterReadingFields } from "Components/MeterInfoForm/SolarMeterReading";

import {
  applicationIdsForChangeMeter,
  applicationTypes,
  meterKeys,
  meterReadingKeys,
  otherMeterId,
} from "common/constant";
import { getOptionValue } from "common/enums";

import { filterEmptyStrings } from "utils";
import filterEmptyObjects from "utils/filterEmptyObjects";
import generateValidationSchema from "utils/generateValidationSchema";

import {
  useGetMeterInfoByApplicationIdQuery,
  useLazyGetMeterDetailsByMeterId,
  useLazyGetMeterDetailsByTypeId,
  usePostMeterInfoMutation,
} from "apis/UtilityConsumerMeter";
import { APPLICATION_DETAILS } from "tagTypes";

const typeWiseMeterKeys = {
  101: [
    "instantaneousVoltageV",
    "instantaneousCurrentA",
    "instantaneousActivePowerkW",
    "activeImportEnergy",
    "activeExportEnergy",
    "currentImportMD",
    "currentExportMD",
  ],
  105: [
    "rPhaseVoltage",
    "yPhaseVoltage",
    "bPhaseVoltage",
    "rPhaseCurrent",
    "yPhaseCurrent",
    "bPhaseCurrent",
    "neutralCurrent",
    "instActivePower",
    "instApparentPower",
    "instantaneousPF",
    "frequency",
    "activeImportEnergy",
    "activeExportEnergy",
    "apparentImportEnergy",
    "apparentExportEnergy",
    "reactiveEnergyQ1",
    "reactiveEnergyQ2",
    "reactiveEnergyQ3",
    "reactiveEnergyQ4",
    "currentImportMD",
    "currentExportMD",
    "cumulativeTamperCount",
  ],
  501: [
    "rPhaseVoltage",
    "yPhaseVoltage",
    "bPhaseVoltage",
    "rPhaseCurrent",
    "yPhaseCurrent",
    "bPhaseCurrent",
    "neutralCurrent",
    "instActivePower",
    "instApparentPower",
    "instantaneousPF",
    "frequency",
    "activeImportEnergy",
    "activeExportEnergy",
    "apparentImportEnergy",
    "apparentExportEnergy",
    "reactiveEnergyQ1",
    "reactiveEnergyQ2",
    "reactiveEnergyQ3",
    "reactiveEnergyQ4",
    "currentApparentImportMD",
    "currentApparentExportMD",
    "currentImportMD",
    "currentExportMD",
    "averagePowerFactor",
    "phaseSequence",
    "currentActiveImportPeakHourEnergyMWhTOD1",
    "currentActiveImportPeakHourEnergyMWhTOD5",
    "currentActiveExportPeakHourEnergyMWh",
    "currentActiveImportNightHourEnergyMWh",
    "currentActiveExportNightHourEnergyMWh",
    "currentActiveImportRestHourEnergyMWh",
    "currentActiveExportRestHourEnergyMWh",
    "hist1ActiveImportEnergy",
    "hist1ActiveExportEnergy",
    "hist2ActiveImportEnergy",
    "hist2ActiveExportEnergy",
    "hist1ActiveImportMD",
    "hist1ActiveExportMD",
    "hist2ActiveImportMD",
    "hist2ActiveExportMD",
    "hist1ApparentImportMD",
    "hist1ApparentExportMD",
    "hist2ApparentImportMD",
    "hist2ApparentExportMD",
    "currentDefrauded",
    "cumulativeTamperCount",
    "activeExportSetOffEnergyTOD3",
    "activeImportSetOffEnergyTOD3",
    "activeExportSetOffEnergyTOD1",
    "activeImportSetOffEnergyTOD1",
  ],
  505: [
    "rPhaseVoltage",
    "yPhaseVoltage",
    "bPhaseVoltage",
    "rPhaseCurrent",
    "yPhaseCurrent",
    "bPhaseCurrent",
    "neutralCurrent",
    "instActivePower",
    "instApparentPower",
    "instantaneousPF",
    "frequency",
    "activeImportEnergy",
    "activeExportEnergy",
    "apparentImportEnergy",
    "apparentExportEnergy",
    "reactiveEnergyQ1",
    "reactiveEnergyQ2",
    "reactiveEnergyQ3",
    "reactiveEnergyQ4",
    "currentApparentImportMD",
    "currentApparentExportMD",
    "currentImportMD",
    "currentExportMD",
    "averagePowerFactor",
    "phaseSequence",
    "currentActiveImportPeakHourEnergyMWhTOD1",
    "currentActiveImportPeakHourEnergyMWhTOD5",
    "currentActiveExportPeakHourEnergyMWh",
    "currentActiveImportNightHourEnergyMWh",
    "currentActiveExportNightHourEnergyMWh",
    "currentActiveImportRestHourEnergyMWh",
    "currentActiveExportRestHourEnergyMWh",
    "hist1ActiveImportEnergy",
    "hist1ActiveExportEnergy",
    "hist2ActiveImportEnergy",
    "hist2ActiveExportEnergy",
    "hist1ActiveImportMD",
    "hist1ActiveExportMD",
    "hist2ActiveImportMD",
    "hist2ActiveExportMD",
    "hist1ApparentImportMD",
    "hist1ApparentExportMD",
    "hist2ApparentImportMD",
    "hist2ApparentExportMD",
    "currentDefrauded",
    "cumulativeTamperCount",
    "activeExportSetOffEnergyTOD3",
    "activeImportSetOffEnergyTOD3",
    "activeExportSetOffEnergyTOD1",
    "activeImportSetOffEnergyTOD1",
  ],
};

const MeterReadingModal = ({
  applicationTypeId,
  applicationId,
  isSolarMeter,
  handleClose,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentMeter, setCurrentMeter] = useState();
  const [meterType, setMeterType] = useState(
    getOptionValue("meterTypes", "Liberty 200")
  );
  const [currentStepName, setCurrentStepName] = useState("Consumer Details");

  const { data: { data: meterDetails } = {}, isLoading: isMeterInfoLoading } =
    useGetMeterInfoByApplicationIdQuery(applicationId);
  const [getMeterByMeterId, { isLoading: isUtilityConsumerMeterLoading }] =
    useLazyGetMeterDetailsByMeterId();
  const [getMeterDetails, { isLoading: isMeterDetailsLoading }] =
    useLazyGetMeterDetailsByTypeId();
  const [updateMeterInfo, { isLoading }] = usePostMeterInfoMutation();

  const {
    utilityConsumer,
    finalMeterReading,
    initialMeterReading,
    solarInitialMeterReadingDetail,
    solarFinalMeterReadingDetail,
  } = useMemo(() => meterDetails || {}, [meterDetails]);

  const { meter, seal, serviceConnection, accucheckTest } = currentMeter || {};
  const { meterDisplayDetail, accucheckDetail, meterReadingTest } =
    accucheckTest || {};

  const initialValues = useMemo(
    () => ({
      consumerInfo: {
        id: utilityConsumer?.id ?? "",
        name: utilityConsumer?.consumerName ?? "",
        address: utilityConsumer?.utilityAddress ?? "",
        serviceNo: utilityConsumer?.consumerNumber ?? "",
        typeOfConnection: utilityConsumer?.typeOfConnection ?? "",
        contractLoad: utilityConsumer?.contractLoad ?? "",
        typeOfTariff: utilityConsumer?.tariffName ?? "",
        meter: {
          id: utilityConsumer?.meter?.id ?? "",
          srNo: utilityConsumer?.meter?.srNo ?? "",
        },
      },
      meter: {
        id: meter?.id ?? 0,
        make: meter?.make ?? "",
        type: meter?.type ?? "",
        srNo: meter?.srNo ?? "",
        labNo: meter?.labNo ?? "",
        phaseOrWire: meter?.phaseOrWire ?? "",
        voltageRating: meter?.voltageRating ?? "",
        currentRating: meter?.currentRating ?? "",
        meterCTR: meter?.meterCTR ?? "",
        meterPTR: meter?.meterPTR ?? "",
        pulseOrkwH: meter?.pulseOrkwH ?? "",
        class: meter?.class ?? "",
        dialMF: meter?.dialMF ?? "",
      },
      seal: {
        id: seal?.id ?? 0,
        meterBodySealLeftBottom: seal?.meterBodySealLeftBottom ?? "",
        meterBodySealLeftTop: seal?.meterBodySealLeftTop ?? "",
        meterBodySealRightTop: seal?.meterBodySealRightTop ?? "",
        meterBodySealRightBottom: seal?.meterBodySealRightBottom ?? "",
        meterTerminalCoverSealLeft: seal?.meterTerminalCoverSealLeft ?? "",
        meterTerminalCoverSealRight: seal?.meterTerminalCoverSealRight ?? "",
        meterResetButtonSeal: seal?.meterResetButtonSeal ?? "",
        meterBoxOrPanelSealTop: seal?.meterBoxOrPanelSealTop ?? "",
        meterBoxOrPanelSealMiddle: seal?.meterBoxOrPanelSealMiddle ?? "",
        meterBoxOrPanelSealBottom: seal?.meterBoxOrPanelSealBottom ?? "",
        testTerminalBlockSealLeft: seal?.testTerminalBlockSealLeft ?? "",
        testTerminalBlockSealRight: seal?.testTerminalBlockSealRight ?? "",
        lineCTSealRPhase: seal?.lineCTSealRPhase ?? "",
        lineCTSealYPhase: seal?.lineCTSealYPhase ?? "",
        lineCTSealBPhase: seal?.lineCTSealBPhase ?? "",
        ptChangeOverBoxSealTop: seal?.ptChangeOverBoxSealTop ?? "",
        ptChangeOverBoxSealBottom: seal?.ptChangeOverBoxSealBottom ?? "",
      },
      serviceConnection: {
        id: serviceConnection?.id ?? 0,
        lineCTRatio: serviceConnection?.lineCTRatio ?? "",
        linePTRatio: serviceConnection?.linePTRatio ?? "",
        rPhase: "",
        yPhase: "",
        bPhase: "",
        finalMF: serviceConnection?.finalMF ?? "",
      },
      accucheckTest: {
        meterDisplayDetail: {
          id: meterDisplayDetail?.id ?? 0,
          rn: meterDisplayDetail?.rn ?? "",
          yn: meterDisplayDetail?.yn ?? "",
          bn: meterDisplayDetail?.bn ?? "",
          kw: meterDisplayDetail?.kw ?? "",
          ir: meterDisplayDetail?.ir ?? "",
          iy: meterDisplayDetail?.iy ?? "",
          ib: meterDisplayDetail?.ib ?? "",
          pf: meterDisplayDetail?.pf ?? "",
        },
        accucheckDetail: {
          id: accucheckDetail?.id ?? 0,
          rn: accucheckDetail?.rn ?? "",
          yn: accucheckDetail?.yn ?? "",
          bn: accucheckDetail?.bn ?? "",
          pf: accucheckDetail?.pf ?? "",
          ir: accucheckDetail?.ir ?? "",
          iy: accucheckDetail?.iy ?? "",
          ib: accucheckDetail?.ib ?? "",
          hz: accucheckDetail?.hz ?? "",
          phaseSequence: accucheckDetail?.phaseSequence ?? "",
          p_KW: accucheckDetail?.p_KW ?? "",
          q_KVAR: accucheckDetail?.q_KVAR ?? "",
          s_KVA: accucheckDetail?.s_KVA ?? "",
          f_KWH: accucheckDetail?.f_KWH ?? "",
        },
        meterReadingTest: {
          id: meterReadingTest?.id ?? 0,
          final: meterReadingTest?.final ?? "",
          initial: meterReadingTest?.initial ?? "",
          difference: meterReadingTest?.difference ?? "",
          error: meterReadingTest?.error ?? "",
        },
      },
      finalMeterReading: {
        id: finalMeterReading?.id ?? 0,
        instantaneousVoltageV: finalMeterReading?.instantaneousVoltageV ?? "",
        instantaneousCurrentA: finalMeterReading?.instantaneousCurrentA ?? "",
        instantaneousActivePowerkW:
          finalMeterReading?.instantaneousActivePowerkW ?? "",
        activeImportEnergy: finalMeterReading?.activeImportEnergy ?? "",
        activeExportEnergy: finalMeterReading?.activeExportEnergy ?? "",
        currentImportMD: finalMeterReading?.currentImportMD ?? "",
        currentExportMD: finalMeterReading?.currentExportMD ?? "",
        rPhaseVoltage: finalMeterReading?.rPhaseVoltage ?? "",
        yPhaseVoltage: finalMeterReading?.yPhaseVoltage ?? "",
        bPhaseVoltage: finalMeterReading?.bPhaseVoltage ?? "",
        rPhaseCurrent: finalMeterReading?.rPhaseCurrent ?? "",
        yPhaseCurrent: finalMeterReading?.yPhaseCurrent ?? "",
        bPhaseCurrent: finalMeterReading?.bPhaseCurrent ?? "",
        neutralCurrent: finalMeterReading?.neutralCurrent ?? "",
        instActivePower: finalMeterReading?.instActivePower ?? "",
        instApparentPower: finalMeterReading?.instApparentPower ?? "",
        instantaneousPF: finalMeterReading?.instantaneousPF ?? "",
        frequency: finalMeterReading?.frequency ?? "",
        apparentImportEnergy: finalMeterReading?.apparentImportEnergy ?? "",
        apparentExportEnergy: finalMeterReading?.apparentExportEnergy ?? "",
        reactiveEnergyQ1: finalMeterReading?.reactiveEnergyQ1 ?? "",
        reactiveEnergyQ2: finalMeterReading?.reactiveEnergyQ2 ?? "",
        reactiveEnergyQ3: finalMeterReading?.reactiveEnergyQ3 ?? "",
        reactiveEnergyQ4: finalMeterReading?.reactiveEnergyQ4 ?? "",
        currentApparentImportMD:
          finalMeterReading?.currentApparentImportMD ?? "",
        currentApparentExportMD:
          finalMeterReading?.currentApparentExportMD ?? "",
        averagePowerFactor: finalMeterReading?.averagePowerFactor ?? "",
        phaseSequence: finalMeterReading?.phaseSequence ?? "",
        currentActiveImportPeakHourEnergyMWhTOD1:
          finalMeterReading?.currentActiveImportPeakHourEnergyMWhTOD1 ?? "",
        currentActiveImportPeakHourEnergyMWhTOD5:
          finalMeterReading?.currentActiveImportPeakHourEnergyMWhTOD5 ?? "",
        currentActiveExportPeakHourEnergyMWh:
          finalMeterReading?.currentActiveExportPeakHourEnergyMWh ?? "",
        currentActiveImportNightHourEnergyMWh:
          finalMeterReading?.currentActiveImportNightHourEnergyMWh ?? "",
        currentActiveExportNightHourEnergyMWh:
          finalMeterReading?.currentActiveExportNightHourEnergyMWh ?? "",
        currentActiveImportRestHourEnergyMWh:
          finalMeterReading?.currentActiveImportRestHourEnergyMWh ?? "",
        currentActiveExportRestHourEnergyMWh:
          finalMeterReading?.currentActiveExportRestHourEnergyMWh ?? "",
        hist1ActiveImportEnergy:
          finalMeterReading?.hist1ActiveImportEnergy ?? "",
        hist1ActiveExportEnergy:
          finalMeterReading?.hist1ActiveExportEnergy ?? "",
        hist2ActiveImportEnergy:
          finalMeterReading?.hist2ActiveImportEnergy ?? "",
        hist2ActiveExportEnergy:
          finalMeterReading?.hist2ActiveExportEnergy ?? "",
        hist1ActiveImportMD: finalMeterReading?.hist1ActiveImportMD ?? "",
        hist1ActiveExportMD: finalMeterReading?.hist1ActiveExportMD ?? "",
        hist2ActiveImportMD: finalMeterReading?.hist2ActiveImportMD ?? "",
        hist2ActiveExportMD: finalMeterReading?.hist2ActiveExportMD ?? "",
        hist1ApparentImportMD: finalMeterReading?.hist1ApparentImportMD ?? "",
        hist1ApparentExportMD: finalMeterReading?.hist1ApparentExportMD ?? "",
        hist2ApparentImportMD: finalMeterReading?.hist2ApparentImportMD ?? "",
        hist2ApparentExportMD: finalMeterReading?.hist2ApparentExportMD ?? "",
        currentDefrauded: finalMeterReading?.currentDefrauded ?? "",
        cumulativeTamperCount: finalMeterReading?.cumulativeTamperCount ?? "",
        activeExportSetOffEnergyTOD1:
          finalMeterReading?.activeExportSetOffEnergyTOD1 ?? "",
        activeImportSetOffEnergyTOD1:
          finalMeterReading?.activeImportSetOffEnergyTOD1 ?? "",
        activeExportSetOffEnergyTOD3:
          finalMeterReading?.activeExportSetOffEnergyTOD3 ?? "",
        activeImportSetOffEnergyTOD3:
          finalMeterReading?.activeImportSetOffEnergyTOD3 ?? "",
      },
      finalSolarMeterReadingDetail: {
        id: solarFinalMeterReadingDetail?.id ?? 0,
        solarEnergy: solarFinalMeterReadingDetail?.solarEnergy ?? 0,
      },
      initialSolarMeterReadingDetail: {
        id: solarInitialMeterReadingDetail?.id ?? 0,
        solarEnergy: solarInitialMeterReadingDetail?.solarEnergy ?? 0,
      },

      initialMeterReading: {
        id: initialMeterReading?.id ?? 0,
        instantaneousVoltageV: initialMeterReading?.instantaneousVoltageV ?? "",
        instantaneousCurrentA: initialMeterReading?.instantaneousCurrentA ?? "",
        instantaneousActivePowerkW:
          initialMeterReading?.instantaneousActivePowerkW ?? "",
        activeImportEnergy: initialMeterReading?.activeImportEnergy ?? "",
        activeExportEnergy: initialMeterReading?.activeExportEnergy ?? "",
        currentImportMD: initialMeterReading?.currentImportMD ?? "",
        currentExportMD: initialMeterReading?.currentExportMD ?? "",
        rPhaseVoltage: initialMeterReading?.rPhaseVoltage ?? "",
        yPhaseVoltage: initialMeterReading?.yPhaseVoltage ?? "",
        bPhaseVoltage: initialMeterReading?.bPhaseVoltage ?? "",
        rPhaseCurrent: initialMeterReading?.rPhaseCurrent ?? "",
        yPhaseCurrent: initialMeterReading?.yPhaseCurrent ?? "",
        bPhaseCurrent: initialMeterReading?.bPhaseCurrent ?? "",
        neutralCurrent: initialMeterReading?.neutralCurrent ?? "",
        instActivePower: initialMeterReading?.instActivePower ?? "",
        instApparentPower: initialMeterReading?.instApparentPower ?? "",
        instantaneousPF: initialMeterReading?.instantaneousPF ?? "",
        frequency: initialMeterReading?.frequency ?? "",
        apparentImportEnergy: initialMeterReading?.apparentImportEnergy ?? "",
        apparentExportEnergy: initialMeterReading?.apparentExportEnergy ?? "",
        reactiveEnergyQ1: initialMeterReading?.reactiveEnergyQ1 ?? "",
        reactiveEnergyQ2: initialMeterReading?.reactiveEnergyQ2 ?? "",
        reactiveEnergyQ3: initialMeterReading?.reactiveEnergyQ3 ?? "",
        reactiveEnergyQ4: initialMeterReading?.reactiveEnergyQ4 ?? "",
        currentApparentImportMD:
          initialMeterReading?.currentApparentImportMD ?? "",
        currentApparentExportMD:
          initialMeterReading?.currentApparentExportMD ?? "",
        averagePowerFactor: initialMeterReading?.averagePowerFactor ?? "",
        phaseSequence: initialMeterReading?.phaseSequence ?? "",
        currentActiveImportPeakHourEnergyMWhTOD1:
          initialMeterReading?.currentActiveImportPeakHourEnergyMWhTOD1 ?? "",
        currentActiveImportPeakHourEnergyMWhTOD5:
          initialMeterReading?.currentActiveImportPeakHourEnergyMWhTOD5 ?? "",
        currentActiveExportPeakHourEnergyMWh:
          initialMeterReading?.currentActiveExportPeakHourEnergyMWh ?? "",
        currentActiveImportNightHourEnergyMWh:
          initialMeterReading?.currentActiveImportNightHourEnergyMWh ?? "",
        currentActiveExportNightHourEnergyMWh:
          initialMeterReading?.currentActiveExportNightHourEnergyMWh ?? "",
        currentActiveImportRestHourEnergyMWh:
          initialMeterReading?.currentActiveImportRestHourEnergyMWh ?? "",
        currentActiveExportRestHourEnergyMWh:
          initialMeterReading?.currentActiveExportRestHourEnergyMWh ?? "",
        hist1ActiveImportEnergy:
          initialMeterReading?.hist1ActiveImportEnergy ?? "",
        hist1ActiveExportEnergy:
          initialMeterReading?.hist1ActiveExportEnergy ?? "",
        hist2ActiveImportEnergy:
          initialMeterReading?.hist2ActiveImportEnergy ?? "",
        hist2ActiveExportEnergy:
          initialMeterReading?.hist2ActiveExportEnergy ?? "",
        hist1ActiveImportMD: initialMeterReading?.hist1ActiveImportMD ?? "",
        hist1ActiveExportMD: initialMeterReading?.hist1ActiveExportMD ?? "",
        hist2ActiveImportMD: initialMeterReading?.hist2ActiveImportMD ?? "",
        hist2ActiveExportMD: initialMeterReading?.hist2ActiveExportMD ?? "",
        hist1ApparentImportMD: initialMeterReading?.hist1ApparentImportMD ?? "",
        hist1ApparentExportMD: initialMeterReading?.hist1ApparentExportMD ?? "",
        hist2ApparentImportMD: initialMeterReading?.hist2ApparentImportMD ?? "",
        hist2ApparentExportMD: initialMeterReading?.hist2ApparentExportMD ?? "",
        currentDefrauded: initialMeterReading?.currentDefrauded ?? "",
        cumulativeTamperCount: initialMeterReading?.cumulativeTamperCount ?? "",
        activeExportSetOffEnergyTOD1:
          finalMeterReading?.activeExportSetOffEnergyTOD1 ?? "",
        activeImportSetOffEnergyTOD1:
          finalMeterReading?.activeImportSetOffEnergyTOD1 ?? "",
        activeExportSetOffEnergyTOD3:
          finalMeterReading?.activeExportSetOffEnergyTOD3 ?? "",
        activeImportSetOffEnergyTOD3:
          finalMeterReading?.activeImportSetOffEnergyTOD3 ?? "",
      },
      extraValues: {
        isMeterChanged: !!(applicationIdsForChangeMeter.includes(
          applicationTypeId
        ) &&
        utilityConsumer?.meter?.id &&
        meter?.id
          ? meter.id !== utilityConsumer.meter.id
          : true),

        meterId: meter?.id ?? otherMeterId,
        srNo: meter?.srNo ?? "",
      },
    }),
    [meterDetails, currentMeter, meter]
  );

  const validationSchemas = useMemo(
    () => ({
      "Consumer Details": Yup.object(),
      "Meter Details": Yup.object({
        meter: Yup.object(generateValidationSchema(meterDetailsFields())),
      }),
      "Seal Details": Yup.object({
        seal: Yup.object(generateValidationSchema(sealDetailsFields)),
      }),
      "Service Connection Details": Yup.object({
        serviceConnection: Yup.object(
          generateValidationSchema(serviceConnectionDetailsFields)
        ),
      }),
      "Accucheck Test": Yup.object({
        accucheckTest: Yup.object(
          generateValidationSchema(accucheckTestFields)
        ),
      }),
      "Initial Meter Reading": Yup.object({
        initialMeterReading: Yup.object(
          generateValidationSchema(meterReadingFields[meterType])
        ),
      }),

      "Initial Solar Reading": Yup.object({
        initialSolarMeterReadingDetail: Yup.object(
          generateValidationSchema(solarMeterReadingFields)
        ),
      }),

      "Final Solar Reading": Yup.object({
        finalSolarMeterReadingDetail: Yup.object(
          generateValidationSchema(solarMeterReadingFields)
        ),
      }),
      "Final Meter Reading": Yup.object({
        finalMeterReading: Yup.object(
          generateValidationSchema(meterReadingFields[meterType])
        ),
      }),
    }),
    [meterType]
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: validationSchemas[currentStepName],
    initialValues,
    onSubmit: async (values, { setTouched }) => {
      const isLastStep = currentStep === steps.length - 1;

      if (isLastStep) {
        await submitBtnHandler(values, false);
        handleClose();
      } else {
        setCurrentStep((prev) => prev + 1);
        setTouched({});
      }
    },
  });

  const {
    values,
    values: {
      meter: { type, id },
      extraValues: { isMeterChanged, meterId },
    },
    setFieldValue,
    handleSubmit,
  } = formik;

  const steps = useMemo(() => {
    const initialSteps = [
      {
        id: 1,
        name: "Consumer Details",
        isCompleted: currentStep >= 1,
        component: (
          <ConsumerDetails
            formik={formik}
            applicationTypeId={applicationTypeId}
            applicationId={applicationId}
          />
        ),
      },
      {
        id: 2,
        name: "Meter Details",
        isCompleted: currentStep >= 2,
        component: <MeterDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Seal Details",
        isCompleted: currentStep >= 3,
        component: <SealDetails formik={formik} />,
      },
      {
        id: 4,
        name: "Service Connection Details",
        isCompleted: currentStep >= 4,
        component: <ServiceConnectionDetails formik={formik} />,
      },
      {
        id: 5,
        name: "Accucheck Test",
        isCompleted: currentStep >= 5,
        component: <AccucheckTest formik={formik} />,
      },
    ];
    if (isSolarMeter) {
      initialSteps.push({
        id: 6,
        name: "Initial Solar Reading",
        isCompleted: currentStep >= 6,
        component: (
          <SolarMeterReading
            parentKey="initialSolarMeterReadingDetail"
            formik={formik}
          />
        ),
      });
    } else {
      if (applicationIdsForChangeMeter.includes(applicationTypeId)) {
        // If meter changed
        if (
          isMeterChanged &&
          meter?.id !== utilityConsumer?.meter?.id &&
          applicationTypeId !== applicationTypes["Removal of Connection"]
        ) {
          initialSteps.splice(
            initialSteps.length,
            0,
            {
              id: 6,
              name: "Final Meter Reading",
              isCompleted: currentStep >= 6,
              component: (
                <MeterReading
                  parentKey="finalMeterReading"
                  formik={formik}
                  initialMeterReading={initialMeterReading || {}}
                  finalMeterReading={finalMeterReading || {}}
                />
              ),
            },
            {
              id: 7,
              name: "Initial Meter Reading",
              isCompleted: currentStep >= 7,
              component: (
                <MeterReading
                  parentKey="initialMeterReading"
                  formik={formik}
                  initialMeterReading={initialMeterReading || {}}
                  finalMeterReading={finalMeterReading || {}}
                />
              ),
            }
          );
        } else {
          initialSteps.push({
            id: 6,
            name: "Final Meter Reading",
            isCompleted: currentStep >= 6,
            component: (
              <MeterReading
                parentKey="finalMeterReading"
                formik={formik}
                initialMeterReading={initialMeterReading || {}}
                finalMeterReading={finalMeterReading || {}}
              />
            ),
          });
        }
      } else {
        // If permanent connection or temporary connection
        initialSteps.push({
          id: 6,
          name: "Initial Meter Reading",
          isCompleted: currentStep >= 6,
          component: (
            <MeterReading
              parentKey="initialMeterReading"
              formik={formik}
              initialMeterReading={initialMeterReading || {}}
              finalMeterReading={finalMeterReading || {}}
            />
          ),
        });
      }
    }

    return initialSteps;
  }, [meter, utilityConsumer, currentStep, formik, applicationTypeId]);

  const getMeterDetailsByMeterId = async (meterId) => {
    try {
      const meterDetails = await getMeterByMeterId(meterId);

      const meterInfo = pick(meterDetails.data, [
        "meter",
        "seal",
        "serviceConnection",
        "accucheckTest",
      ]);

      setCurrentMeter(meterInfo);
    } catch (error) {
      console.error(error);
    }
  };

  const submitBtnHandler = async (values, isDraft = false) => {
    const {
      meter: { type },
    } = values;

    const payloadValues = {
      ...omit(values, "initialMeterReading , finalMeterReading"),
      finalMeterReading: { id: values.finalMeterReading.id },
      initialMeterReading: { id: values.initialMeterReading.id },
    };

    typeWiseMeterKeys[type]?.forEach((key) => {
      payloadValues.finalMeterReading[key] = values.finalMeterReading[key];
      payloadValues.initialMeterReading[key] = values.initialMeterReading[key];
    });

    const filteredValues = filterEmptyStrings(
      omit(payloadValues, ["consumerInfo", "extraValues"])
    );

    let finalPayloadValues = {};
    if (applicationIdsForChangeMeter.includes(applicationTypeId)) {
      // If meter changed
      if (isMeterChanged && meter?.id !== utilityConsumer?.meter?.id) {
        finalPayloadValues = filteredValues;
      } else {
        finalPayloadValues = omit(filteredValues, "initialMeterReading");
      }
    } else {
      // If permanent connection or temporary connection
      finalPayloadValues = omit(filteredValues, "finalMeterReading");
    }

    const finalPayload = filterEmptyObjects(finalPayloadValues);
    try {
      await updateMeterInfo({
        query: { applicationId },
        payload: {
          ...filterEmptyStrings(
            omit(
              finalPayload,
              isSolarMeter
                ? [
                    "finalSolarMeterReadingDetail",
                    "initialMeterReading",
                    "finalMeterReading",
                  ]
                : [
                    "finalSolarMeterReadingDetail",
                    "initialSolarMeterReadingDetail",
                  ]
            )
          ),
          isDraft,
        },
        toastMessage: "Meter details updated successfully",
        tags: [APPLICATION_DETAILS],
      });
    } catch (error) {
      console.error(error);
    }
  };

  const saveBtnHandler = () => {
    submitBtnHandler(values, true);
  };

  useEffect(() => {
    if (!id || type !== meterDetails?.meter?.type) {
      (async () => {
        try {
          const { data } = await getMeterDetails({
            meterVariantType: type,
          });
          meterKeys.forEach((key) =>
            setFieldValue(`meter.${key}`, get(data, key) ?? "")
          );
        } catch (error) {}
      })();
      meterReadingKeys.forEach(({ key }) => {
        setFieldValue(`finalMeterReading.${key}`, "");
        setFieldValue(`initialMeterReading.${key}`, "");
      });
    }
    setMeterType(type);
  }, [type]);

  useEffect(() => {
    const stepName = steps.find((_, index) => index === currentStep).name;
    if (stepName !== currentStepName) {
      setCurrentStepName(stepName);
    }
  }, [currentStep, steps]);

  // changeMeter
  useEffect(() => {
    if (!isMeterChanged && utilityConsumer?.meter?.id) {
      getMeterDetailsByMeterId(utilityConsumer.meter.id);
    }
  }, [isMeterChanged]);

  // change meterInfo if meter is changed
  useEffect(() => {
    if (meterId === otherMeterId) {
      setCurrentMeter(null);
    } else if (currentMeter?.meter?.id !== meterId) {
      getMeterDetailsByMeterId(meterId);
    }
  }, [meterId]);

  // set initial meter info
  useEffect(() => {
    if (meterDetails?.meter?.id) {
      getMeterDetailsByMeterId(meterDetails.meter.id);
    } else if (utilityConsumer?.meter?.id) {
      getMeterDetailsByMeterId(utilityConsumer.meter.id);
    }
  }, [meterDetails]);

  if (
    isMeterInfoLoading ||
    isUtilityConsumerMeterLoading ||
    isMeterDetailsLoading
  ) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen={!!applicationId}
      handleClose={handleClose}
      title="Meter Information"
      maxWidth="lg"
    >
      <Stepper
        steps={steps}
        handleSubmit={handleSubmit}
        activeStepIndex={currentStep}
        setActiveStepIndex={setCurrentStep}
        isLoading={isLoading}
        formik={formik}
        handleDraft={saveBtnHandler}
        style={{ minHeight: "300px" }}
      />
    </Modal>
  );
};

MeterReadingModal.propTypes = {
  applicationId: PropTypes.number,
  isSolarMeter: PropTypes.bool,
  handleClose: PropTypes.func,
  applicationTypeId: PropTypes.number,
};

export default MeterReadingModal;
